import {useState, useEffect} from 'react';
import moment from 'moment';
import Select from 'react-select';
import {router} from '../../App';
import {connect} from 'react-redux';
import sidebarRoutes from '../../routes';
import {canOpen} from '../../internals/config/moduleAccess';
import {NavLink} from 'react-router-dom';
import Logo from '../../assets/svgs/logo.svg';
import {HiOutlineMenuAlt2} from 'react-icons/hi';
import {Actions} from '../../internals/app/Actions';
import {Tooltip as ReactTooltip} from 'react-tooltip';
import {get, isEmpty, filter, findIndex} from 'lodash';
import WarrantyView from '../../modules/warranty/views/WarrantyView';
import {AiOutlineClose, AiOutlineDown, AiOutlineRight} from 'react-icons/ai';

// Heroicons v2 outline — one unique, semantically correct icon per route
import {
  HomeIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  UsersIcon,
  UserGroupIcon,
  ChartBarIcon,
  DocumentTextIcon,
  TableCellsIcon,
  TagIcon,
  SparklesIcon,
  GiftIcon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  IdentificationIcon,
  BanknotesIcon,
  CheckBadgeIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

// ─── Icon map ─────────────────────────────────────────────────────────────────
// Maps each sidebar route name to the appropriate Heroicon component.
const ROUTE_ICONS: Record<string, React.ComponentType<{className?: string}>> = {
  Dashboard:     HomeIcon,
  Products:      ShoppingBagIcon,
  Services:      WrenchScrewdriverIcon,
  Courses:       AcademicCapIcon,
  Employees:     BriefcaseIcon,
  Customers:     UsersIcon,
  Users:         UserGroupIcon,
  Reports:       ChartBarIcon,
  Transaction:   DocumentTextIcon,
  Complementary: GiftIcon,
  Tables:        TableCellsIcon,
  Discounts:     TagIcon,
  Offer:         SparklesIcon,
  Download:      ArrowDownTrayIcon,
  Settings:      Cog6ToothIcon,
  // Netly Pay. These sit in the groups that match what they do rather than
  // in a section of their own — see docs/design/netly-pay/DESIGN-BRIEF.md.
  Members:              IdentificationIcon,
  'Payment requests':   BanknotesIcon,
  Approvals:            CheckBadgeIcon,
  Records:              BookOpenIcon,
  Programs:             CalendarDaysIcon,
  Attendance:           ClipboardDocumentCheckIcon,
};

// ─── Nav grouping (visual only — routing/permissions untouched) ───────────────
// Section labels per the redesign; any route not listed falls into "More".
// A group with no routes is dropped below, so the Netly Pay groups stay
// invisible until their screens exist and the shop is linked.
const NAV_GROUPS: {label: string; names: string[]}[] = [
  {label: 'General',  names: ['Dashboard', 'Reports', 'Transaction']},
  {label: 'Catalog',  names: ['Products', 'Services', 'Courses', 'Tables']},
  {label: 'People',   names: ['Employees', 'Customers', 'Members', 'Users', 'Complementary']},
  {label: 'Billing',  names: ['Payment requests', 'Approvals', 'Records']},
  {label: 'Schedule', names: ['Programs', 'Attendance']},
  {label: 'Marketing', names: ['Discounts', 'Offer']},
  {label: 'System',   names: ['Download', 'Settings']},
];

const NavIcon = ({
  name,
  isActive,
  size = 'md',
}: {
  name: string;
  isActive: boolean;
  size?: 'sm' | 'md';
}) => {
  const Icon = ROUTE_ICONS[name] ?? HomeIcon;
  const sizeClass = size === 'sm' ? 'w-[18px] h-[18px]' : 'w-[17px] h-[17px]';
  const colorClass = isActive ? 'text-brand' : 'text-text3';
  return <Icon className={`${sizeClass} ${colorClass} flex-shrink-0`} />;
};

// ─── Shop Select Styles (design tokens via CSS variables) ─────────────────────
const shopSelectStyles = {
  control: (base: any) => ({
    ...base,
    background: 'var(--surface2)',
    borderColor: 'var(--border)',
    borderRadius: '11px',
    boxShadow: 'none',
    fontSize: '13px',
    minHeight: '46px',
    cursor: 'pointer',
    '&:hover': {borderColor: 'var(--border2)'},
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: '13px',
    backgroundColor: state.isSelected
      ? 'var(--brandSoft)'
      : state.isFocused
      ? 'var(--surface2)'
      : 'var(--surface)',
    color: state.isSelected ? 'var(--brand)' : 'var(--text)',
    fontWeight: state.isSelected ? 600 : 400,
    cursor: 'pointer',
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: '11px',
    backgroundColor: 'var(--surface)',
    boxShadow: 'var(--shadowLg)',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: 'var(--text)',
    fontWeight: 600,
  }),
  input: (base: any) => ({
    ...base,
    color: 'var(--text)',
  }),
  indicatorSeparator: () => ({display: 'none'}),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: 'var(--text3)',
    padding: '0 8px',
  }),
};

// Gradient avatar + STORE label rendering inside the select control / menu.
const formatShopOption = (opt: any, meta: {context: string}) => (
  <div className="flex items-center gap-2.5 min-w-0">
    <div
      className="w-[26px] h-[26px] rounded-lg text-white flex items-center justify-center text-xs font-bold flex-shrink-0"
      style={{background: 'linear-gradient(140deg, var(--brand), var(--brand2))'}}
    >
      {(opt.label || '?').charAt(0).toUpperCase()}
    </div>
    <div className="min-w-0">
      {meta.context === 'value' && (
        <div className="text-[9px] tracking-[0.1em] text-text3 font-semibold leading-none mb-0.5">
          STORE
        </div>
      )}
      <div className="text-[13px] font-semibold truncate leading-tight">
        {opt.label}
      </div>
    </div>
  </div>
);

// ─── Sidebar Component ────────────────────────────────────────────────────────
const Sidebar = (props: {
  userClaims: any;
  shopClaims: any;
  adminShops: any;
  currentUser: any;
  selectedShop: any;
  trialDetails: any;
  logout: () => void;
  openSidebar: boolean;
  selectedRoute: string;
  selectedAdminShop: any;
  selectedSubRoute: string;
  activeSubscriptions: any;
  shopModules: any;
  toggleOpenDrawer: () => void;
  setSelectedRoute: (route: string) => void;
  onSelectDashBoard: (name: string) => void;
  setSelectedSubRoute: (route: string) => void;
}) => {
  const {
    logout,
    userClaims,
    shopClaims,
    adminShops,
    currentUser,
    openSidebar,
    trialDetails,
    selectedShop,
    selectedRoute,
    toggleOpenDrawer,
    setSelectedRoute,
    selectedSubRoute,
    onSelectDashBoard,
    selectedAdminShop,
    activeSubscriptions,
    shopModules,
    setSelectedSubRoute,
  } = props;

  const [activeShop, setActiveShop] = useState<any>([]);
  const [routes, setRoutes] = useState<any>([]);

  const shopCategory = selectedShop.shopCategory;

  // ── Warranty route injection for RETAILS ──────────────────────────────────
  useEffect(() => {
    if (selectedShop) {
      const data = [...sidebarRoutes];
      if (selectedShop.shopCategory === 'RETAILS') {
        let index = findIndex(data, (admin: any) => admin.name === 'Products');
        if (
          data[index].subCategory?.findIndex(
            ({path}: any) => path === '/warranty',
          ) === -1
        ) {
          data[index].subCategory?.push({
            path: '/warranty',
            name: 'Warranty',
            rtlName: 'Warranty Management',
            component: WarrantyView,
            layout: '/admin',
          });
        }
      }
      setRoutes([...data]);
    }
  }, [selectedShop]);

  // ── Active shop list ──────────────────────────────────────────────────────
  useEffect(() => {
    let shops: any = [];
    userClaims.map((claim: any) => {
      if (
        get(claim, 'claimType', null) === 'ACTIVE' ||
        get(claim, 'claimType', null) === null
      ) {
        const filterShop = filter(
          adminShops,
          (shop: any) => shop.shopId === claim.shopId,
        );
        shops = [...shops, ...filterShop];
      }
    });
    setActiveShop(shops);
  }, [userClaims, adminShops]);

  const trialPeriod = moment(trialDetails?.trialEnds).diff(moment(), 'days');

  const isActiveSubscription =
    activeSubscriptions?.filter(({status}: any) => status === 'SUCCESS')
      .length > 0;

  // A screen is restricted when the shop does not have the modules it needs
  // (src/internals/config/moduleAccess.ts). The server decides which modules a
  // shop has, from its trial and its subscriptions, so the trial needs no
  // special case here any more.
  //
  // `shopModules` is null while the answer is still loading or if it could not
  // be fetched, and canOpen() then allows the screen: a dashboard that cannot
  // check must not lock a paying customer out of their own business. The
  // whole-app subscription gate in layouts/Admin is unchanged.
  const getRestrict = (item: any) => !canOpen(item?.path, shopModules);

  // ── Footer user display ───────────────────────────────────────────────────
  const userName =
    [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') ||
    currentUser?.name ||
    currentUser?.email ||
    'Account';
  const userInitials = userName
    .split(' ')
    .map((p: string) => p.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // ── Sub-category children ─────────────────────────────────────────────────
  const renderChildren = (route: any) => {
    if (selectedSubRoute !== route.name || isEmpty(route.subCategory)) {
      return null;
    }
    return (
      <ul className="mt-1 ml-5 pl-3 border-l border-borderc space-y-0.5">
        {route.subCategory.map((sub: any) => {
          const restricted = getRestrict(sub);
          if (restricted) return null;
          return (
            <li key={sub.path}>
              <NavLink
                to={sub.path}
                onClick={() => {
                  if (!restricted) {
                    setSelectedRoute(sub.name);
                    onSelectDashBoard(sub.rtlName);
                  }
                }}
              >
                {({isActive}: {isActive: boolean}) => (
                  <div
                    className={`flex items-center px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer ${
                      isActive
                        ? 'bg-brand-soft text-brand font-semibold'
                        : 'text-text3 hover:bg-surface2 hover:text-text1'
                    }`}
                  >
                    <span className="text-[12.5px] font-medium">{sub.name}</span>
                  </div>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    );
  };

  // ── Nav leaf item ─────────────────────────────────────────────────────────
  const renderNavItem = (route: any, isActive: boolean) => {
    const restricted = getRestrict(route);
    return (
      <div
        className={`flex items-center gap-[11px] px-2.5 py-[9px] rounded-[10px] transition-colors ${
          isActive ? 'bg-brand-soft' : 'hover:bg-surface2'
        }`}
      >
        <NavIcon name={route.name} isActive={isActive} />
        <span
          className={`flex-1 text-[13.5px] leading-none tracking-[-0.01em] lg:block ${
            isActive ? 'text-brand font-semibold' : 'text-text2'
          } ${openSidebar ? 'block' : 'hidden'}`}
        >
          {route.name}
        </span>
        {restricted && (
          <>
            <span
              data-tooltip-id={`pro-${route.path}`}
              className={`bg-brand text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none lg:block ${
                openSidebar ? 'block' : 'hidden'
              }`}
            >
              PRO
            </span>
            <ReactTooltip
              id={`pro-${route.path}`}
              place="right"
              variant="info"
              content="Upgrade your plan to access this feature"
            />
          </>
        )}
      </div>
    );
  };

  // ── Nav group (expandable with sub-items) ─────────────────────────────────
  const renderNavGroup = (route: any) => {
    const isOpen = selectedSubRoute === route.name;
    return (
      <>
        <div
          onClick={() => {
            router.navigate(route.path);
            setSelectedRoute(route.name);
            setSelectedSubRoute(isOpen ? '' : route.name);
          }}
          className={`flex items-center gap-[11px] px-2.5 py-[9px] rounded-[10px] cursor-pointer transition-colors ${
            isOpen ? 'bg-brand-soft' : 'hover:bg-surface2'
          }`}
        >
          <NavIcon name={route.name} isActive={isOpen} />
          <span
            className={`flex-1 text-[13.5px] leading-none tracking-[-0.01em] lg:block ${
              isOpen ? 'text-brand font-semibold' : 'text-text2'
            } ${openSidebar ? 'block' : 'hidden'}`}
          >
            {route.name}
          </span>
          <span
            className={`text-text3 transition-transform duration-200 lg:block ${
              openSidebar ? 'block' : 'hidden'
            }`}
          >
            {isOpen ? <AiOutlineDown size={11} /> : <AiOutlineRight size={11} />}
          </span>
        </div>
        {renderChildren(route)}
      </>
    );
  };

  // ── Route list renderer ───────────────────────────────────────────────────
  const renderRouteList = (routeList: any[]) =>
    routeList.map((route: any) => {
      const isOpen = selectedRoute === route.name;
      const isActiveSub = !isEmpty(route.subCategory);
      const restricted = getRestrict(route);

      if (
        shopCategory !== 'HOSPITALITY' &&
        shopCategory !== 'FOOD_AND_BEVERAGES' &&
        route.name === 'Tables'
      ) {
        return null;
      }

      if (restricted) {
        return <li key={route.path}>{renderNavItem(route, isOpen)}</li>;
      }

      return (
        <li key={route.path}>
          {isActiveSub ? (
            renderNavGroup(route)
          ) : (
            <NavLink
              to={route.path}
              onClick={() => {
                setSelectedSubRoute('');
                setSelectedRoute(route.name);
                onSelectDashBoard(route.rtlName);
              }}
            >
              {({isActive}: {isActive: boolean}) =>
                renderNavItem(route, isActive || isOpen)
              }
            </NavLink>
          )}
        </li>
      );
    });

  // ── Grouped nav (design sections) ─────────────────────────────────────────
  const knownNames = new Set(NAV_GROUPS.flatMap((g) => g.names));
  const groupedNav = NAV_GROUPS.map((g) => ({
    label: g.label,
    items: g.names
      .map((n) => routes.find((r: any) => r.name === n))
      .filter(Boolean),
  })).filter((g) => g.items.length > 0);
  const ungrouped = routes.filter((r: any) => !knownNames.has(r.name));
  if (ungrouped.length > 0) {
    groupedNav.push({label: 'More', items: ungrouped});
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Compact icon-only column (md screens, sidebar not open) ─────── */}
      <div
        className={`h-full flex flex-col py-4 gap-1 items-center ${
          openSidebar ? 'hidden' : 'flex'
        } lg:hidden`}
      >
        {/* Expand toggle */}
        <button
          type="button"
          onClick={() => toggleOpenDrawer()}
          className="flex w-9 h-9 justify-center items-center rounded-xl bg-brand hover:bg-brand2 transition-colors mb-3"
        >
          <HiOutlineMenuAlt2 color="#fff" size={18} />
        </button>

        {/* Icon list */}
        <div className="flex-1 overflow-y-auto w-full flex flex-col gap-1 items-center px-1">
          {routes.map((route: any) => {
            const isOpen = selectedRoute === route.name;
            const isActiveSub = !isEmpty(route.subCategory);
            const restricted = getRestrict(route);

            if (
              shopCategory !== 'HOSPITALITY' &&
              shopCategory !== 'FOOD_AND_BEVERAGES' &&
              route.name === 'Tables'
            ) {
              return null;
            }

            const iconBtn = (isActive: boolean) => (
              <div
                data-tooltip-id={`tip-${route.path}`}
                className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors ${
                  isActive ? 'bg-brand-soft' : 'hover:bg-surface2'
                }`}
              >
                <NavIcon name={route.name} isActive={isActive} size="sm" />
                <ReactTooltip
                  id={`tip-${route.path}`}
                  place="right"
                  variant="light"
                  content={route.name}
                  style={{fontSize: '12px', fontWeight: 500}}
                />
              </div>
            );

            if (restricted || isActiveSub) {
              return (
                <div
                  key={route.path}
                  onClick={() => {
                    router.navigate(route.path);
                    setSelectedRoute(route.name);
                    setSelectedSubRoute(route.name);
                  }}
                  className="cursor-pointer"
                >
                  {iconBtn(isOpen)}
                </div>
              );
            }

            return (
              <NavLink
                key={route.path}
                to={route.path}
                onClick={() => {
                  setSelectedSubRoute('');
                  setSelectedRoute(route.name);
                  onSelectDashBoard(route.rtlName);
                }}
              >
                {({isActive}: {isActive: boolean}) =>
                  iconBtn(isActive || isOpen)
                }
              </NavLink>
            );
          })}
        </div>

        {/* Logout */}
        <button
          type="button"
          onClick={() => logout()}
          data-tooltip-id="tip-logout-compact"
          className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-surface2 transition-colors mt-2"
        >
          <ArrowRightOnRectangleIcon className="w-[18px] h-[18px] text-text3" />
          <ReactTooltip
            id="tip-logout-compact"
            place="right"
            variant="light"
            content="Logout"
            style={{fontSize: '12px', fontWeight: 500}}
          />
        </button>
      </div>

      {/* ── Full sidebar (lg+ always, md when openSidebar=true) ──────────── */}
      <div
        className={`flex flex-col h-full overflow-y-auto transition-all duration-300 ease-in-out bg-surface border-r border-borderc ${
          openSidebar
            ? 'w-[252px] fixed z-50 shadow-pop'
            : 'hidden lg:flex lg:w-[252px]'
        }`}
      >
        {/* ── Header: logo + wordmark + POS chip ─────────────────────────── */}
        <div className="flex items-center gap-2.5 px-[18px] pt-5 pb-3.5">
          <NavLink
            to="/dashboard"
            onClick={() => setSelectedRoute('/dashboard')}
            className="flex items-center gap-2.5 min-w-0 flex-1"
          >
            <img
              src={Logo}
              className="w-[30px] h-[30px] rounded-[9px] flex-shrink-0"
              alt="Netlise"
            />
            <span className="font-semibold text-text1 text-base tracking-[-0.02em] truncate">
              Netlise
            </span>
          </NavLink>

          <span className="text-[10px] font-semibold tracking-[0.06em] text-text3 border border-borderc px-1.5 py-[3px] rounded-md flex-shrink-0">
            POS
          </span>

          {openSidebar && (
            <button
              type="button"
              onClick={() => toggleOpenDrawer()}
              className="flex w-7 h-7 items-center justify-center rounded-lg text-text3 hover:bg-surface2 hover:text-text1 transition-colors flex-shrink-0"
            >
              <AiOutlineClose size={14} />
            </button>
          )}
        </div>

        {/* ── Store selector ──────────────────────────────────────────────── */}
        {selectedShop && (
          <div className="px-3.5 pb-3.5">
            <Select
              styles={shopSelectStyles}
              formatOptionLabel={formatShopOption}
              defaultValue={{
                label: selectedShop?.shopName,
                value: selectedShop?.shopId,
              }}
              options={activeShop?.map((shop: any) => ({
                label: shop.shopName,
                value: shop.shopId,
              }))}
              onChange={({value}: any) => {
                const shop = activeShop.find(
                  ({shopId}: any) => shopId === value,
                );
                selectedAdminShop(shop);
                setSelectedRoute('Dashboard');
                setSelectedSubRoute('Dashboard');
              }}
              className="w-full text-sm"
              isSearchable={false}
            />
          </div>
        )}

        {/* ── Navigation (grouped sections) ───────────────────────────────── */}
        <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-4">
          {groupedNav.map((group) => (
            <div key={group.label}>
              <p className="text-[9.5px] font-semibold tracking-[0.12em] text-text3 uppercase px-2.5 pb-1.5">
                {group.label}
              </p>
              <ul className="space-y-0.5">{renderRouteList(group.items)}</ul>
            </div>
          ))}
        </nav>

        {/* ── Footer: trial + user row ────────────────────────────────────── */}
        <div className="border-t border-borderc px-3 pt-3 pb-3 space-y-2">
          {/* Trial badge */}
          {!isActiveSubscription && trialPeriod > 0 && (
            <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-brand-soft">
              <span className="w-2 h-2 rounded-full bg-brand flex-shrink-0" />
              <p className="text-xs font-medium text-brand leading-none">
                Trial: {trialPeriod} day{trialPeriod !== 1 ? 's' : ''} remaining
              </p>
            </div>
          )}

          {/* User row + logout */}
          <div className="flex items-center gap-2.5 px-1">
            <div className="w-[30px] h-[30px] rounded-full bg-surface3 flex items-center justify-center text-[11px] font-bold text-text2 flex-shrink-0">
              {userInitials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-semibold text-text1 truncate leading-tight">
                {userName}
              </p>
              <p className="text-[11px] text-text3 truncate leading-tight">
                {selectedShop?.shopName || 'Netlise POS'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => logout()}
              title="Log out"
              className="p-1.5 rounded-lg text-text3 hover:bg-surface2 hover:text-text1 transition-colors flex-shrink-0"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: any) => ({
    shopClaims: state.user.get('shopClaims'),
    userClaims: state.user.get('userClaims'),
    adminShops: state.dashboard.get('adminShops'),
    currentUser: state.setting.get('currentUser'),
    trialDetails: state.setting.get('trialDetails'),
    selectedShop: state.dashboard.get('selectedShop'),
    activeSubscriptions: state.setting.get('activeSubscriptions'),
    shopModules: state.setting.get('shopModules'),
  }),
  {
    selectedAdminShop: Actions.dashboard.selectedAdminShop,
  },
)(Sidebar);
